import React from 'react';
import { bool, func, number, oneOf, oneOfType, string } from 'prop-types';
import { inRange, isEmpty } from 'lodash';
import classNames from 'classnames';
import { BASE_CLASS, MIN_RATING, MAX_RATING, COLORS, THEMES } from './constants';
import { formatRatingsCount, formatScore, formatStars } from './utils';
import Star from './Star';

import './index.scss';

/**
 * @deprecated Please use Rating from @fiverr-private/data_display instead.
 */
const Rating = ({
    className,
    starsClassName,
    starClassName,
    scoreClassName,
    ratingsCountClassName,
    score,
    scoreMinimumFractionDigits,
    scoreMaximumFractionDigits,
    showScore,
    ratingsCount,
    theme,
    color,
    onClick,
    onRatingsClick
}) => {
    if (!inRange(score, MIN_RATING, MAX_RATING)) {return null;}

    const stars = formatStars(theme, score);
    const showRatingsCount = ratingsCount > 0 || !isEmpty(ratingsCount);

    const wrapperClasses = classNames(
        BASE_CLASS,
        className,
        `color-${color}`,
        'tbody-6'
    );

    const starsClasses = classNames(
        'stars',
        starsClassName
    );

    const scoreClasses = classNames(
        'rating-score',
        scoreClassName
    );

    const ratingsCountClasses = classNames(
        'ratings-count',
        ratingsCountClassName,
        { 'clickable': !!onRatingsClick }
    );

    return (
        <div className={wrapperClasses} onClick={onClick}>
            <div className={starsClasses}>
                {stars.map((theme, index) => (
                    <Star key={index}
                        className={starClassName}
                        theme={theme}/>
                ))}
            </div>
            {showScore && (
                <strong className={scoreClasses}>
                    {formatScore(score, {
                        minimumFractionDigits: scoreMinimumFractionDigits,
                        maximumFractionDigits: scoreMaximumFractionDigits
                    })}
                </strong>
            )}
            {showRatingsCount && (
                <span className={ratingsCountClasses} onClick={onRatingsClick}>
                    (<span className="rating-count-number">{formatRatingsCount(ratingsCount)}</span>)
                </span>
            )}
        </div>
    );
};

Rating.displayName = 'Rating';

Rating.propTypes = {
    /**
     * A className to be appended
     */
    className: string,
    /**
     * A className to be appended to stars container
     */
    starsClassName: string,
    /**
     * A className to be appended to star icon
     */
    starClassName: string,
    /**
     * A className to be appended to score
     */
    scoreClassName: string,
    /**
     * A className to be appended to ratings count
     */
    ratingsCountClassName: string,
    /**
     * A float value between 0 and 5 to indicate the user rating.
     */
    score: number,
    /**
     * Whether to show the ratings score or not.
     */
    showScore: bool,
    /**
     * The minimum number of fraction digits to use for score.
     */
    scoreMinimumFractionDigits: number,
    /**
     * The maximum number of fraction digits to use for score.
     */
    scoreMaximumFractionDigits: number,
    /**
     * Indicating the total amount of ratings.
     */
    ratingsCount: oneOfType([string, number]),
    /**
     * The rating theme.
     * Supported options are exposed through the static `THEMES` property.
     */
    theme: oneOf(Object.values(THEMES)),
    /**
     * Custom color for star and score.
     */
    color: oneOf(Object.values(COLORS)),
    /**
     * Callback when rating is clicked.
     */
    onClick: func,
    /**
     * Callback when rating is clicked.
     */
    onRatingsClick: func
};

Rating.defaultProps = {
    score: 0,
    showScore: true,
    theme: THEMES.FIVE_STARS,
    color: COLORS.YELLOW,
    onClick: Function.prototype
};

Object.assign(Rating, {
    THEMES,
    COLORS
});

export default Rating;
